import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Search, Filter, Edit, Trash2, Calendar, Users,
  Video, Clock, Play, CheckCircle, X, AlertCircle
} from 'lucide-react'
import { useTimezone } from '../../hooks/useTimezone'
import { istDateTimeLocalToUTC } from '../../utils/timezone'

interface LiveSession {
  _id: string
  title: string
  description: string
  instructor: string
  scheduledAt: string
  duration: number
  meetingLink: string
  meetingPassword?: string
  maxParticipants: number
  registeredUsers: any[]
  category: string
  status: 'scheduled' | 'ongoing' | 'completed' | 'cancelled'
  isActive: boolean
  recordingUrl?: string
}

export default function AdminLiveSessions() {
  const navigate = useNavigate()
  const { formatDate, formatDateTime, formatDateTimeLocal } = useTimezone()
  const [sessions, setSessions] = useState<LiveSession[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  // State for comprehensive update modal
  const [updateModal, setUpdateModal] = useState<{
    isOpen: boolean;
    sessionId: string;
    session: LiveSession | null;
  }>({ isOpen: false, sessionId: '', session: null })
  const [updateData, setUpdateData] = useState({
    meetingLink: '',
    meetingPassword: '',
    scheduledAt: '',
    duration: 60
  })
  const [updateLoading, setUpdateLoading] = useState(false)

  useEffect(() => {
    fetchSessions()
  }, [])

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/live-sessions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setSessions(data.sessions || [])
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (sessionId: string) => {
    if (!confirm('Are you sure you want to delete this session?')) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/live-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setSessions(sessions.filter(s => s._id !== sessionId))
        alert('Session deleted successfully')
      }
    } catch (error) {
      console.error('Error deleting session:', error)
      alert('Error deleting session')
    }
  }

  // Functions for comprehensive updates
  const openUpdateModal = (session: LiveSession) => {
    setUpdateModal({ isOpen: true, sessionId: session._id, session })
    setUpdateData({
      meetingLink: session.meetingLink || '',
      meetingPassword: session.meetingPassword || '',
      scheduledAt: formatDateTimeLocal(session.scheduledAt),
      duration: session.duration || 60
    })
  }

  const closeUpdateModal = () => {
    setUpdateModal({ isOpen: false, sessionId: '', session: null })
    setUpdateData({
      meetingLink: '',
      meetingPassword: '',
      scheduledAt: '',
      duration: 60
    })
    setUpdateLoading(false)
  }

  const handleSessionUpdate = async () => {
    if (!updateModal.sessionId || !updateModal.session) return

    setUpdateLoading(true)
    try {
      // Build payload with only fields that should be updated
      const payload: any = {
        meetingLink: updateData.meetingLink,
        meetingPassword: updateData.meetingPassword,
        duration: updateData.duration
      }

      // Only include scheduledAt if it has a value and time component
      if (updateData.scheduledAt && updateData.scheduledAt.includes('T')) {
        payload.scheduledAt = istDateTimeLocalToUTC(updateData.scheduledAt)
      }

      const response = await fetch(`/api/admin/live-sessions/${updateModal.sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const updatedSession = await response.json()
        setSessions(sessions.map(s =>
          s._id === updateModal.sessionId
            ? {
              ...s,
              meetingLink: updateData.meetingLink,
              meetingPassword: updateData.meetingPassword,
              scheduledAt: updateData.scheduledAt,
              duration: updateData.duration
            }
            : s
        ))
        alert('Session updated successfully')
        closeUpdateModal()
      } else {
        alert('Error updating session')
      }
    } catch (error) {
      console.error('Error updating session:', error)
      alert('Error updating session')
    }
    setUpdateLoading(false)
  }

  // Check if session spans multiple days
  const isMultiDaySession = (session: LiveSession) => {
    const scheduledDate = new Date(session.scheduledAt)
    const endDate = new Date(scheduledDate.getTime() + (session.duration * 60000))
    return scheduledDate.toDateString() !== endDate.toDateString()
  }

  // Check if session has already started
  const hasSessionStarted = (session: LiveSession) => {
    return new Date() > new Date(session.scheduledAt)
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      scheduled: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
      ongoing: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
      completed: 'bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400',
      cancelled: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
    }
    return badges[status as keyof typeof badges] || badges.scheduled
  }

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.instructor.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = filterStatus === 'all' || session.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const stats = {
    total: sessions.length,
    scheduled: sessions.filter(s => s.status === 'scheduled').length,
    ongoing: sessions.filter(s => s.status === 'ongoing').length,
    completed: sessions.filter(s => s.status === 'completed').length,
    totalParticipants: sessions.reduce((sum, s) => sum + (s.registeredUsers?.length || 0), 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <Video className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.total}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl">
              <Calendar className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.scheduled}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Scheduled</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
              <Play className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.ongoing}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Ongoing</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl">
              <CheckCircle className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.completed}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Completed</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
              <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">{stats.totalParticipants}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Participants</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <button
            onClick={() => navigate('/admin/live-sessions/new')}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
          >
            <Plus className="h-5 w-5" />
            <span>Schedule Session</span>
          </button>
        </div>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-12 border border-gray-100 dark:border-dark-700 text-center">
          <div className="w-20 h-20 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <Video className="h-10 w-10 text-charcoal-400" />
          </div>
          <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
            No sessions found
          </h3>
          <p className="text-charcoal-500 dark:text-charcoal-400 mb-8">
            Schedule your first live session to get started
          </p>
          <button
            onClick={() => navigate('/admin/live-sessions/new')}
            className="px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20"
          >
            Schedule Session
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredSessions.map((session) => (
            <div
              key={session._id}
              className="group bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 hover:shadow-xl transition-all duration-300 hover:border-rose-200 dark:hover:border-rose-900/30"
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="text-xl font-bold text-charcoal-900 dark:text-white group-hover:text-rose-600 transition-colors">
                      {session.title}
                    </h3>
                    <span className={`px-2.5 py-1 text-xs font-bold rounded-lg backdrop-blur-md ${getStatusBadge(session.status)}`}>
                      {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </span>
                    <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                      {session.category}
                    </span>
                  </div>
                  <p className="text-charcoal-600 dark:text-charcoal-300 mb-6 leading-relaxed">
                    {session.description}
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-charcoal-600 dark:text-charcoal-400 bg-gray-50 dark:bg-dark-900/50 px-3 py-2 rounded-lg">
                      <Calendar className="h-4 w-4 text-rose-500" />
                      {formatDate(session.scheduledAt)}
                    </div>
                    <div className="flex items-center gap-2 text-charcoal-600 dark:text-charcoal-400 bg-gray-50 dark:bg-dark-900/50 px-3 py-2 rounded-lg">
                      <Clock className="h-4 w-4 text-rose-500" />
                      {formatDateTime(session.scheduledAt, { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {/* Only show participants if not unlimited */}
                    {session.maxParticipants && session.maxParticipants > 0 ? (
                      <div className="flex items-center gap-2 text-charcoal-600 dark:text-charcoal-400 bg-gray-50 dark:bg-dark-900/50 px-3 py-2 rounded-lg">
                        <Users className="h-4 w-4 text-rose-500" />
                        {session.registeredUsers?.length || 0}/{session.maxParticipants}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-charcoal-600 dark:text-charcoal-400 bg-gray-50 dark:bg-dark-900/50 px-3 py-2 rounded-lg">
                        <Users className="h-4 w-4 text-rose-500" />
                        {session.registeredUsers?.length || 0} registered
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-charcoal-600 dark:text-charcoal-400 bg-gray-50 dark:bg-dark-900/50 px-3 py-2 rounded-lg">
                      <Clock className="h-4 w-4 text-rose-500" />
                      {session.duration} minutes
                    </div>
                  </div>

                  {/* Registration Deadline */}
                  {(session as any).registrationDeadline && (
                    <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-900/30 inline-flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                        Registration Deadline: {formatDateTime((session as any).registrationDeadline, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex md:flex-col gap-2 min-w-[140px]">
                  {session.meetingLink && (
                    <button
                      onClick={() => window.open(session.meetingLink, '_blank')}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-green-600/20"
                    >
                      <Play className="h-4 w-4" />
                      Join
                    </button>
                  )}

                  <button
                    onClick={() => openUpdateModal(session)}
                    className="flex-1 px-4 py-2 bg-white dark:bg-dark-700 text-charcoal-700 dark:text-charcoal-200 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Video className="h-4 w-4" />
                    Update
                  </button>

                  <button
                    onClick={() => navigate(`/admin/live-sessions/edit/${session._id}`)}
                    className="flex-1 px-4 py-2 bg-white dark:bg-dark-700 text-charcoal-700 dark:text-charcoal-200 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(session._id)}
                    className="flex-1 px-4 py-2 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comprehensive Update Modal */}
      {updateModal.isOpen && updateModal.session && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 dark:border-dark-700 animate-scale-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-charcoal-900 dark:text-white">
                Update Session Details
              </h3>
              <button
                onClick={closeUpdateModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-charcoal-500" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Meeting Link */}
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Meeting Link
                </label>
                <input
                  type="url"
                  value={updateData.meetingLink}
                  onChange={(e) => setUpdateData({ ...updateData, meetingLink: e.target.value })}
                  placeholder="Enter meeting link..."
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                />
              </div>

              {/* Meeting Passcode */}
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Meeting Passcode
                </label>
                <input
                  type="text"
                  value={updateData.meetingPassword}
                  onChange={(e) => setUpdateData({ ...updateData, meetingPassword: e.target.value })}
                  placeholder="Enter passcode (optional)..."
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                />
              </div>

              {/* Timing Updates */}
              <div className="space-y-4">
                <div className="border-t border-gray-100 dark:border-dark-700 pt-6">
                  <h4 className="text-lg font-bold text-charcoal-900 dark:text-white mb-4">
                    Session Timing
                  </h4>

                  {isMultiDaySession(updateModal.session) ? (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl mb-4">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        <strong>Multi-day session:</strong> You can update timing anytime before the session starts.
                      </p>
                    </div>
                  ) : hasSessionStarted(updateModal.session) ? (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl mb-4">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        <strong>Single-day session:</strong> This session has already started. Timing updates are not allowed.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl mb-4">
                      <p className="text-sm font-medium text-green-800 dark:text-green-300">
                        <strong>Single-day session:</strong> You can update timing only before the session starts.
                      </p>
                    </div>
                  )}

                  {/* Only show timing fields if allowed */}
                  {(isMultiDaySession(updateModal.session) || !hasSessionStarted(updateModal.session)) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                          Scheduled Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          value={updateData.scheduledAt}
                          onChange={(e) => setUpdateData({ ...updateData, scheduledAt: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          min="15"
                          max="480"
                          value={updateData.duration}
                          onChange={(e) => setUpdateData({ ...updateData, duration: parseInt(e.target.value) || 60 })}
                          className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={closeUpdateModal}
                disabled={updateLoading}
                className="px-6 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700 text-charcoal-600 dark:text-charcoal-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSessionUpdate}
                disabled={updateLoading || !updateData.meetingLink.trim()}
                className="px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {updateLoading ? 'Updating...' : 'Update Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
